<template>
  <div class="relative">
    <button 
      @click="toggleDropdown"
      class="flex gap-[12px] items-center justify-between w-full px-2.5 py-2 text-left bg-white hover:bg-gray-50 focus:outline-none max-w-fit-content rounded-md transition-all duration-200"
      :class="[buttonClass, { 'bg-blue-50': isOpen }]"
    >
      <slot name="trigger" :isOpen="isOpen" :totalCount="totalCount">
        <!-- Default trigger content -->
        <span class="text-blue-light font-jost-bold">
          {{ totalCount || items.length }}
        </span>
        <i :class="isOpen ? 'fa fa-angle-up' : 'fa fa-angle-down'" class="text-blue-light text-sm transition-transform duration-200"></i>
      </slot>
    </button>
    
    <transition name="dropdown">
      <div 
        v-if="isOpen" 
        ref="dropdownContainer"
        @scroll="enableInfiniteScrolling ? handleScroll : null"
        class="dropdown-container absolute z-20 w-full min-w-[280px] mt-2 bg-white rounded-lg shadow-xl max-h-64 overflow-y-auto"
        :class="dropdownClass"
      >
      <div 
        v-for="(item, index) in items" 
        :key="getItemKey(item, index)"
        class="px-4 py-3 last:border-b-0 hover:bg-blue-50 transition-colors duration-200 cursor-pointer"
        :class="itemClass"
      >
        <slot name="item" :item="item" :index="index">
          <!-- Default item content -->
          <div class="flex justify-between items-center">
            <div>
              <div class="font-medium text-blue-dark text-sm font-jost-regular">
                {{ item.label || item.name || item.type }}
              </div>
              <div class="text-xs text-gray-500 mt-1 font-jost-regular" v-if="item.subtitle">
                {{ item.subtitle }}
              </div>
            </div>
            <div class="text-right">
              <div class="font-semibold text-blue-dark font-jost-bold text-sm">
                {{ item.value || item.amount }}
              </div>
            </div>
          </div>
        </slot>
      </div>
      
      <!-- Loading indicator -->
      <div v-if="enableInfiniteScrolling && isLoading" class="px-4 py-3 text-center">
        <div class="flex items-center justify-center gap-2">
          <div class="animate-spin rounded-full h-4 w-4 border-2 border-blue-light border-t-transparent"></div>
          <span class="text-xs text-blue-dark font-jost-regular">Loading more...</span>
        </div>
      </div>
      
      <!-- No more items indicator -->
      <div v-else-if="enableInfiniteScrolling && hasMoreItems === false && items.length > 0" class="px-4 py-2 text-center">
        <div class="text-xs text-gray-500 font-jost-regular">— No more items —</div>
      </div>
      
      <slot name="empty" v-if="items.length === 0 && !(enableInfiniteScrolling && isLoading)">
        <div class="px-4 py-6 text-gray-500 text-sm text-center font-jost-regular">
          <div class="flex flex-col items-center gap-2">
            <i class="fa fa-inbox text-2xl text-gray-400"></i>
            <span>No items found</span>
          </div>
        </div>
      </slot>
    </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "DropdownComponent",
  props: {
    items: {
      type: Array,
      default: () => []
    },
    buttonClass: {
      type: String,
      default: ""
    },
    dropdownClass: {
      type: String,
      default: ""
    },
    itemClass: {
      type: String,
      default: ""
    },
    initialOpen: {
      type: Boolean,
      default: false
    },
    hasMoreItems: {
      type: Boolean,
      default: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: null
    },
    itemKeyField: {
      type: String,
      default: null
    },
    enableInfiniteScrolling: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: this.initialOpen
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      this.$emit('toggle', this.isOpen);
    },
    closeDropdown() {
      this.isOpen = false;
      this.$emit('toggle', this.isOpen);
    },
    openDropdown() {
      this.isOpen = true;
      this.$emit('toggle', this.isOpen);
    },
    handleScroll(event) {
      // Only handle scroll if infinite scrolling is enabled
      if (!this.enableInfiniteScrolling) {
        return;
      }

      const container = event.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      // Check if user has scrolled near the bottom (within 50px)
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        if (this.hasMoreItems && !this.isLoading) {
          this.$emit('load-more');
        }
      }
    },
    getItemKey(item, index) {
      if (this.itemKeyField && item[this.itemKeyField]) {
        return item[this.itemKeyField];
      }
      return index;
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', (event) => {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    });
  }
}
</script>

<style scoped>
/* Custom scrollbar styling to match app design */
.dropdown-container::-webkit-scrollbar {
  width: 6px;
}

.dropdown-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.dropdown-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dropdown-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Smooth dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease-in-out;
  transform-origin: top;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: scaleY(0.95) translateY(-10px);
}

/* Enhanced focus styles */
button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Loading spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
